import { createApp, nextTick, ref, type App } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { driver, type Driver, type DriveStep } from "driver.js";
import "driver.js/dist/driver.css";
import { Role } from "tf-api-client";
import { useAuth } from "./use-auth";
import { isFeatureEnabled } from "../utils/feature-flags";
import { TOUR_COMPLETED_KEY } from "../config/constants";
import FencyTourMessage from "../components/Tour/FencyTourMessage.vue";

type PopoverSide = NonNullable<DriveStep["popover"]>["side"];

type TourStep = {
  // Key under `tour.steps` in the locale files.
  id: string;
  // Page the step lives on; the tour navigates there first. Steps without a
  // route (sidebar, header, centred popovers) show on whatever page is open.
  route?: string;
  // Route whose feature flag gates the step, when it isn't `route`.
  feature?: string;
  // `data-tour` value of the element to highlight; none = centred popover.
  target?: string;
  side?: PopoverSide;
  // The sidebar only exists from lg; below it the header menu button stands in.
  layout?: "desktop" | "mobile";
  // Fency's expression while telling the step (a mood name, see
  // copilot-moods.ts).
  mood?: string;
};

const ASSISTANT = "/protection/assistant";
const SECURE_BROWSER = "/protection/secure-browser";
const COURSES = "/e-learning/courses";

const STEPS: TourStep[] = [
  { id: "welcome", mood: "positive" },
  {
    id: "navAssistant",
    mood: "positive",
    feature: ASSISTANT,
    target: `nav:${ASSISTANT}`,
    side: "right",
    layout: "desktop",
  },
  {
    id: "mobileMenu",
    mood: "informative",
    target: "mobile-menu",
    side: "bottom",
    layout: "mobile",
  },
  {
    id: "assistantInput",
    mood: "curious",
    route: ASSISTANT,
    target: "assistant-input",
    side: "top",
  },
  {
    id: "assistantConversations",
    mood: "informative",
    route: ASSISTANT,
    target: "assistant-conversations",
    side: "bottom",
  },
  {
    id: "navSecureBrowser",
    mood: "warning",
    feature: SECURE_BROWSER,
    target: `nav:${SECURE_BROWSER}`,
    side: "right",
    layout: "desktop",
  },
  {
    id: "secureBrowserInput",
    mood: "assertive",
    route: SECURE_BROWSER,
    target: "secure-browser-input",
    side: "top",
  },
  {
    id: "navCourses",
    mood: "informative",
    feature: COURSES,
    target: `nav:${COURSES}`,
    side: "right",
    layout: "desktop",
  },
  {
    id: "coursesFilters",
    mood: "curious",
    route: COURSES,
    target: "courses-filters",
    side: "bottom",
  },
  {
    id: "coursesList",
    mood: "positive",
    route: COURSES,
    target: "courses-list",
    side: "top",
  },
  {
    id: "userMenu",
    mood: "informative",
    target: "user-menu",
    side: "right",
    layout: "desktop",
  },
  { id: "finish", mood: "conclusive" },
];

// How long to wait for a page's element after navigating before showing the
// step centred instead (e.g. the courses list while it is still loading).
const ELEMENT_TIMEOUT_MS = 3000;

const tourSelector = (target: string) => `[data-tour="${target}"]`;

// The sidebar is rendered twice (desktop + mobile drawer), so a selector can
// match a hidden copy: take the first match that is actually laid out.
const findVisible = (selector: string) =>
  Array.from(document.querySelectorAll(selector)).find(
    (el) => el.getClientRects().length > 0,
  ) ?? null;

const waitForElement = (selector: string) =>
  new Promise<Element | null>((resolve) => {
    const found = findVisible(selector);
    if (found) return resolve(found);

    let timer = 0;
    const observer = new MutationObserver(() => {
      const el = findVisible(selector);
      if (!el) return;
      observer.disconnect();
      window.clearTimeout(timer);
      resolve(el);
    });
    timer = window.setTimeout(() => {
      observer.disconnect();
      resolve(null);
    }, ELEMENT_TIMEOUT_MS);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
    });
  });

const storageKey = (userId: string) => `${TOUR_COMPLETED_KEY}:${userId}`;

const hasCompletedTour = (userId: string) => {
  try {
    return localStorage.getItem(storageKey(userId)) === "true";
  } catch {
    // Storage unavailable: the completion couldn't be saved either, so don't
    // start the tour on every login.
    return true;
  }
};

const markTourCompleted = (userId: string) => {
  try {
    localStorage.setItem(storageKey(userId), "true");
  } catch {
    // Ignore write errors (e.g. storage disabled or quota exceeded).
  }
};

// Shared across callers: the layout auto-starts it, the user menu restarts it.
const isTourActive = ref(false);

export function useDashboardTour() {
  const { t } = useI18n({ useScope: "global" });
  const router = useRouter();
  const { user, selectedMembership } = useAuth();

  const buildSteps = () => {
    const isDesktop = window.matchMedia("(min-width: 1024px)").matches;
    return STEPS.filter((step) => {
      if (step.layout === "desktop" && !isDesktop) return false;
      if (step.layout === "mobile" && isDesktop) return false;
      const feature = step.feature ?? step.route;
      return !feature || isFeatureEnabled(feature);
    });
  };

  // Brings the page of a step on screen and waits for its element to render.
  const prepareStep = async (step: TourStep) => {
    if (step.route && router.currentRoute.value.path !== step.route) {
      await router.push(step.route);
    }
    if (step.target) await waitForElement(tourSelector(step.target));
  };

  const startTour = async () => {
    if (isTourActive.value || !user.value) return;

    const userId = user.value.id;
    const steps = buildSteps();
    if (steps.length === 0) return;

    isTourActive.value = true;
    await nextTick();

    // Fency greets the user by name when the account has one.
    const firstName = user.value.firstName?.trim();
    const stepTitle = (step: TourStep) =>
      step.id === "welcome" && firstName
        ? t("tour.steps.welcome.titleNamed", { name: firstName })
        : t(`tour.steps.${step.id}.title`);

    let message: App | null = null;
    const unmountMessage = () => {
      message?.unmount();
      message = null;
    };
    // Fency cheers up while the pointer is on the primary button.
    const cheering = ref(false);

    // The extension dialogs' scrim (fency-ui-styles.ts), in the active theme.
    const dark = document.documentElement.classList.contains("dark");

    // Moves are async (navigation + render), so ignore clicks while one runs.
    let moving = false;
    const moveTo = async (tour: Driver, index: number) => {
      if (moving) return;
      moving = true;
      try {
        await prepareStep(steps[index]);
        tour.moveTo(index);
      } finally {
        moving = false;
      }
    };

    const tour = driver({
      showProgress: true,
      progressText: t("tour.progress", {
        current: "{{current}}",
        total: "{{total}}",
      }),
      nextBtnText: t("tour.next"),
      prevBtnText: t("tour.prev"),
      doneBtnText: t("tour.done"),
      popoverClass: "tf-tour",
      overlayColor: dark ? "rgb(3, 6, 10)" : "rgb(8, 16, 24)",
      overlayOpacity: dark ? 0.72 : 0.62,
      stagePadding: 6,
      stageRadius: 10,
      // Clicking a highlighted nav item would navigate away mid-step.
      disableActiveInteraction: true,
      steps: steps.map((step) => ({
        element: step.target
          ? () => findVisible(tourSelector(step.target!)) as Element
          : undefined,
        popover: {
          // The title is the dialog header; the description is replaced by
          // FencyTourMessage in onPopoverRender.
          title: stepTitle(step),
          description: t(`tour.steps.${step.id}.body`),
          side: step.side,
          align: "start",
        },
      })),
      // driver.js rebuilds the popover on every step: tell the step as Fency.
      onPopoverRender: (popover, { index }) => {
        const step = steps[index ?? 0];
        unmountMessage();
        cheering.value = false;
        message = createApp(FencyTourMessage, {
          body: t(`tour.steps.${step.id}.body`),
          mood: step.mood,
          cheering,
        });
        message.mount(popover.description);

        // "Fency" above the title, like the extension's dialog header.
        const eyebrow = document.createElement("span");
        eyebrow.className = "tf-tour-eyebrow";
        eyebrow.textContent = "Fency";
        popover.title.prepend(eyebrow);

        // Focus goes to the primary button, as in the extension's dialogs.
        // driver.js focuses the first focusable element right after this
        // hook, so take it back on the next frame; with the close button last
        // (it is positioned absolutely, so nothing moves) Tab reaches it last.
        popover.wrapper.append(popover.closeButton);
        const next = popover.nextButton;
        requestAnimationFrame(() => next.focus({ preventScroll: true }));

        next.addEventListener("mouseenter", () => (cheering.value = true));
        next.addEventListener("mouseleave", () => (cheering.value = false));
      },
      onNextClick: (_element, _step, { driver: d }) => {
        if (d.isLastStep()) {
          d.destroy();
          return;
        }
        void moveTo(d, (d.getActiveIndex() ?? 0) + 1);
      },
      onPrevClick: (_element, _step, { driver: d }) => {
        void moveTo(d, Math.max((d.getActiveIndex() ?? 0) - 1, 0));
      },
      // Finishing and closing early both count as seen: the tour can be
      // replayed from the user menu.
      onDestroyed: () => {
        unmountMessage();
        markTourCompleted(userId);
        isTourActive.value = false;
      },
    });

    // Open the tour's first page behind the (centred) welcome step.
    const firstPage = steps.find((step) => step.route)?.route;
    if (firstPage && router.currentRoute.value.path !== firstPage) {
      await router.push(firstPage);
    }
    await prepareStep(steps[0]);
    tour.drive(0);
  };

  // First login of a standard user: admins (also while viewing as standard)
  // only get the tour from the user menu.
  const autoStartTour = () => {
    if (!user.value || selectedMembership.value?.role !== Role.Standard) return;
    if (hasCompletedTour(user.value.id)) return;
    void startTour();
  };

  return { isTourActive, startTour, autoStartTour };
}
